import React, { useState, useRef, useEffect, useCallback } from 'react';
import { validateForm, encryptData, generateLZID, formatDate, extractRequestorName } from '../../utils/formHandler';
import './GetStarted.css';

const LeaseForm = () => {
  // API config from environment variables
  const CONFIG = {
    apiEndpoint: process.env.REACT_APP_API_ENDPOINT,
    hCaptchaSiteKey: process.env.REACT_APP_HCAPTCHA_SITE_KEY
  };

  // Form state
  const [formData, setFormData] = useState({
    AptURL: '',
    AptName: '',
    AptFP: '',
    AptUN: '',
    AptPrice: '',
    leaseStart: '',
    AptEmail: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');

  // References
  const formRef = useRef(null);
  const submitButtonRef = useRef(null);
  const buttonTextRef = useRef(null);
  const buttonLoaderRef = useRef(null);
  const captchaRef = useRef(null);
  const captchaWidgetId = useRef(null);

  // hCaptcha callbacks
  const onCaptchaVerify = useCallback((token) => {
    console.log('hCaptcha verified');
    setCaptchaToken(token);
  }, []);

  const onCaptchaExpire = useCallback(() => {
    console.log('hCaptcha expired');
    setCaptchaToken('');
  }, []);

  const onCaptchaError = useCallback((err) => {
    console.error('hCaptcha error:', err);
    setCaptchaToken('');
  }, []);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Real-time validation
    if (formData[name] !== value) {
      const fieldError = validateField(name, value);
      setErrors({ ...errors, [name]: fieldError });
    }
  };

  // Validate a single field
  const validateField = (name, value) => {
    const tempErrors = validateForm({ [name]: value });
    return tempErrors[name] || '';
  };

  // Set loading state
  const setLoading = (isLoading) => {
    if (submitButtonRef.current) {
      submitButtonRef.current.disabled = isLoading;
    }
    if (buttonTextRef.current) {
      buttonTextRef.current.style.display = isLoading ? 'none' : 'block';
    }
    if (buttonLoaderRef.current) {
      buttonLoaderRef.current.style.display = isLoading ? 'block' : 'none';
    }
    setIsSubmitting(isLoading);
  };

  // Show error message
  const showError = (message) => {
    setFormError(message);
    setTimeout(() => {
      setFormError('');
    }, 5000);
  };

  // Show success message and redirect
  const showSuccess = () => {
    setFormSuccess(true);
    setFormData({
      AptURL: '',
      AptName: '',
      AptFP: '',
      AptUN: '',
      AptPrice: '',
      leaseStart: '',
      AptEmail: ''
    });

    // Reset hCaptcha
    if (window.hcaptcha && captchaWidgetId.current !== null) {
      window.hcaptcha.reset(captchaWidgetId.current);
    }
    setCaptchaToken('');

    // Redirect to thank you page
    setTimeout(() => {
      window.location.href = '/thank-you';
    }, 1500);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Form submission started');

    // Validate all fields
    const formErrors = validateForm(formData);
    setErrors(formErrors);

    // Check if there are any errors
    if (Object.keys(formErrors).length > 0) {
      return;
    }

    // Validate hCaptcha
    if (!captchaToken) {
      showError('Please complete the captcha verification');
      return;
    }

    setLoading(true);

    try {
      const formattedDate = formatDate(formData.leaseStart);
      const email = formData.AptEmail;
      const requestorName = extractRequestorName(email);

      const rawFormData = {
        [generateLZID()]: {
          ApartmentName: formData.AptName,
          apartmentFloorPlan: formData.AptFP,
          apartmentRequestor: requestorName,
          apartmentTargetPrice: parseFloat(formData.AptPrice),
          apartmentUnitNumber: formData.AptUN,
          apartmentURL: formData.AptURL,
          idealLeaseStart: formattedDate,
          requestorEmail: email
        }
      };

      // Send only encrypted data - no password needed in client
      const encryptedFormData = {
        data: encryptData(rawFormData)
      };

      console.log('Sending request to:', CONFIG.apiEndpoint);

      const response = await fetch(CONFIG.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Captcha-Token': captchaToken
        },
        body: JSON.stringify(encryptedFormData)
      });

      // We don't need the response body - just log that we got a response
      await response.json(); // Consume the response
      console.log('API Response received (status:', response.status, ')');

      // TEMP FIX: Data is saved to DynamoDB even if email sending fails
      // Always redirect to success page regardless of API response status
      // TODO: Migrate Lambda email service from MailerSend to SES
      showSuccess();
    } catch (error) {
      console.error('Submission error:', error);
      showError(`Error: ${error.message}`);
    } finally {
      setLoading(false);
      // Reset captcha after submission attempt
      if (window.hcaptcha && captchaWidgetId.current !== null) {
        window.hcaptcha.reset(captchaWidgetId.current);
      }
      setCaptchaToken('');
    }
  };

  // Load and render hCaptcha
  useEffect(() => {
    // Define callback functions on window for hCaptcha
    window.onLeaseFormCaptchaVerify = onCaptchaVerify;
    window.onLeaseFormCaptchaExpire = onCaptchaExpire;
    window.onLeaseFormCaptchaError = onCaptchaError;

    const renderCaptcha = () => {
      if (window.hcaptcha && captchaRef.current && captchaWidgetId.current === null) {
        try {
          captchaWidgetId.current = window.hcaptcha.render(captchaRef.current, {
            sitekey: CONFIG.hCaptchaSiteKey,
            callback: 'onLeaseFormCaptchaVerify',
            'expired-callback': 'onLeaseFormCaptchaExpire',
            'error-callback': 'onLeaseFormCaptchaError',
          });
          console.log('hCaptcha rendered successfully');
        } catch (err) {
          console.error('Error rendering hCaptcha:', err);
        }
      }
    };

    // Check if hCaptcha script is already loaded
    if (window.hcaptcha) {
      renderCaptcha();
    } else {
      // Load the script
      const script = document.createElement('script');
      script.src = 'https://js.hcaptcha.com/1/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        // Small delay to ensure hcaptcha is ready
        setTimeout(renderCaptcha, 100);
      };
      document.head.appendChild(script);
    }

    return () => {
      // Cleanup
      delete window.onLeaseFormCaptchaVerify;
      delete window.onLeaseFormCaptchaExpire;
      delete window.onLeaseFormCaptchaError;
    };
  }, [CONFIG.hCaptchaSiteKey, onCaptchaVerify, onCaptchaExpire, onCaptchaError]);

  return (
    <form id="leaseForm" className="lease-form" onSubmit={handleSubmit} ref={formRef}>
      {formError && (
        <div id="formErrorMessage" className="error-message" style={{ display: 'block' }}>
          {formError}
        </div>
      )}
      {formSuccess && (
        <div id="formSuccessMessage" className="success-message" style={{ display: 'block' }}>
          Form submitted successfully!
        </div>
      )}

      <div className="form-group">
        <div className="label-container">
          <label htmlFor="AptURL">Apartment URL:</label>
          <div className="info-icon">ⓘ
            <span className="tooltip">Copy and paste the full URL from the apartment listing page. This should be the direct link to the specific unit you want to track.</span>
          </div>
        </div>
        <input
          type="url"
          id="AptURL"
          name="AptURL"
          required
          pattern="https?://.+"
          title="Please enter a valid URL starting with http:// or https://"
          value={formData.AptURL}
          onChange={handleChange}
          className={errors.AptURL ? 'error' : ''}
        />
        {errors.AptURL && (
          <div className="error-message" id="AptURLError" style={{ display: 'block' }}>
            {errors.AptURL}
          </div>
        )}
      </div>

      <div className="form-group">
        <div className="label-container">
          <label htmlFor="AptName">Apartment Name:</label>
          <div className="info-icon">ⓘ
            <span className="tooltip">Enter the official name of the apartment complex or building (e.g., "The Residences at Central Park").</span>
          </div>
        </div>
        <input
          type="text"
          id="AptName"
          name="AptName"
          required
          minLength="2"
          maxLength="100"
          value={formData.AptName}
          onChange={handleChange}
          className={errors.AptName ? 'error' : ''}
        />
        {errors.AptName && (
          <div className="error-message" id="AptNameError" style={{ display: 'block' }}>
            {errors.AptName}
          </div>
        )}
      </div>

      <div className="form-group">
        <div className="label-container">
          <label htmlFor="AptFP">Apartment Floor Plan:</label>
          <div className="info-icon">ⓘ
            <span className="tooltip">Enter the floor plan name or type (e.g., "Studio", "1BR/1BA", "Deluxe 2-Bedroom").</span>
          </div>
        </div>
        <input
          type="text"
          id="AptFP"
          name="AptFP"
          required
          minLength="1"
          maxLength="5"
          value={formData.AptFP}
          onChange={handleChange}
          className={errors.AptFP ? 'error' : ''}
        />
        {errors.AptFP && (
          <div className="error-message" id="AptFPError" style={{ display: 'block' }}>
            {errors.AptFP}
          </div>
        )}
      </div>

      <div className="form-group">
        <div className="label-container">
          <label htmlFor="AptUN">Apartment Unit Number:</label>
          <div className="info-icon">ⓘ
            <span className="tooltip">Enter the specific unit number you're interested in (e.g., "101", "A-204", "PH3").</span>
          </div>
        </div>
        <input
          type="text"
          id="AptUN"
          name="AptUN"
          required
          minLength="1"
          maxLength="20"
          value={formData.AptUN}
          onChange={handleChange}
          className={errors.AptUN ? 'error' : ''}
        />
        {errors.AptUN && (
          <div className="error-message" id="AptUNError" style={{ display: 'block' }}>
            {errors.AptUN}
          </div>
        )}
      </div>

      <div className="form-group">
        <div className="label-container">
          <label htmlFor="AptPrice">Ideal Monthly Lease Price:</label>
          <div className="info-icon">ⓘ
            <span className="tooltip">Enter your target monthly rent. We'll notify you when the price drops significantly or when the price drops below this amount.</span>
          </div>
        </div>
        <input
          type="number"
          id="AptPrice"
          name="AptPrice"
          required
          min="100"
          max="100000"
          step="0.01"
          value={formData.AptPrice}
          onChange={handleChange}
          className={errors.AptPrice ? 'error' : ''}
        />
        {errors.AptPrice && (
          <div className="error-message" id="AptPriceError" style={{ display: 'block' }}>
            {errors.AptPrice}
          </div>
        )}
      </div>

      <div className="form-group">
        <div className="label-container">
          <label htmlFor="leaseStart">Ideal Lease Start Date:</label>
          <div className="info-icon">ⓘ
            <span className="tooltip">Select your preferred move-in date. This helps us track prices for the specific lease term you're interested in.</span>
          </div>
        </div>
        <input
          type="date"
          id="leaseStart"
          name="leaseStart"
          required
          value={formData.leaseStart}
          onChange={handleChange}
          className={errors.leaseStart ? 'error' : ''}
        />
        {errors.leaseStart && (
          <div className="error-message" id="leaseStartError" style={{ display: 'block' }}>
            {errors.leaseStart}
          </div>
        )}
      </div>

      <div className="form-group">
        <div className="label-container">
          <label htmlFor="AptEmail">Email Address:</label>
          <div className="info-icon">ⓘ
            <span className="tooltip">We'll send price alerts and updates to this email address. We don't share your information with third parties.</span>
          </div>
        </div>
        <input
          type="email"
          id="AptEmail"
          name="AptEmail"
          required
          value={formData.AptEmail}
          onChange={handleChange}
          className={errors.AptEmail ? 'error' : ''}
        />
        {errors.AptEmail && (
          <div className="error-message" id="AptEmailError" style={{ display: 'block' }}>
            {errors.AptEmail}
          </div>
        )}
      </div>

      {/* hCaptcha container - explicit render */}
      <div className="form-group captcha-container">
        <div ref={captchaRef}></div>
      </div>

      <button type="submit" className="submit-button" id="submitButton" ref={submitButtonRef} disabled={isSubmitting}>
        <span className="button-text" ref={buttonTextRef}>Submit</span>
        <div className="button-loader" ref={buttonLoaderRef} style={{ display: 'none' }}></div>
      </button>
    </form>
  );
};

export default LeaseForm;
